// POS → Restaurant → Pedidos Externos (/pos/rest-pedidos-ext).
// Agregadores (PedidosYa / UberEats / Rappi): configuración por proveedor con
// api_key CIFRADA (write-only) y bandeja de pedidos. En modo SIMULADO se
// generan pedidos de prueba; el conector real (webhook/worker) llegará con las
// credenciales de partner. Aceptar crea la cuenta DELIVERY con líneas pendientes.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bike, Check, FlaskConical, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { SelectorLocal, Campo, Switch, fieldCls, money, posRpc } from './shared';
import { AgregadorCfgT, PedidoExtT, useLocalRestaurant } from './restaurante';

const PROVEEDORES = ['pedidosya', 'ubereats', 'rappi', 'otro'];

const ESTADO_BADGE: Record<string, string> = {
  nuevo: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 animate-pulse',
  aceptado: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  despachado: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  entregado: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  rechazado: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  cancelado: 'bg-muted text-muted-foreground',
};

export function PedidosExternosRestaurant() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { locationId, setLocationId, locations } = useLocalRestaurant();
  const [fProv, setFProv] = useState('pedidosya');
  const [fModo, setFModo] = useState('simulado');
  const [fKey, setFKey] = useState('');
  const [fCom, setFCom] = useState('25');

  const { data: config = [] } = useQuery<AgregadorCfgT[]>({
    queryKey: ['rest-agreg-cfg', locationId],
    queryFn: () => posRpc<AgregadorCfgT[]>('rest_agregadores_config_listar', { _location: locationId }),
    enabled: !!locationId,
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: pedidos = [] } = useQuery<PedidoExtT[]>({
    queryKey: ['rest-pedidos-ext', locationId],
    queryFn: () => posRpc<PedidoExtT[]>('rest_pedidos_ext_listar', { _location: locationId }),
    enabled: !!locationId,
    staleTime: 0, refetchOnMount: 'always', refetchInterval: 8000,
  });

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['rest-pedidos-ext'] });
    void qc.invalidateQueries({ queryKey: ['rest-agreg-cfg'] });
    void qc.invalidateQueries({ queryKey: ['rest-estado'] });
  };
  const rpc = async (fn: string, body: Record<string, unknown>, ok?: string) => {
    try {
      const r = await posRpc(fn, body);
      invalidar();
      if (ok) toast.success(ok);
      return r;
    } catch (e) { toast.error(getErrorMessage(e)); return null; }
  };

  const aceptar = async (p: PedidoExtT) => {
    const v = await dlg.pedir(`Aceptar ${p.proveedor} ${p.referencia_ext}`, [
      { key: 'pin', label: 'PIN de mesero' },
    ]);
    if (!v?.pin) return;
    const r = await rpc('rest_pedido_ext_aceptar', { _id: p.id, _pin: v.pin });
    if (r) toast.success(`Cuenta delivery creada: ${(r as { codigo: string }).codigo} — comanda desde el Salón`);
  };

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      {dlg.ui}
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Bike className="w-5 h-5" /> Pedidos Externos (agregadores)</h1>
        <SelectorLocal locationId={locationId} setLocationId={setLocationId} locations={locations} />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Bandeja de pedidos</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {pedidos.map((p) => (
            <div key={p.id} className="border rounded-md px-3 py-2 flex items-center gap-3 flex-wrap">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${ESTADO_BADGE[p.estado] ?? ''}`}>{p.estado}</span>
              <span className="font-medium capitalize">{p.proveedor}</span>
              <span className="font-mono text-xs">{p.referencia_ext}</span>
              <span className="text-sm text-muted-foreground">
                {p.payload.cliente} · {p.payload.direccion} · hace {p.minutos}m
              </span>
              <span className="text-xs">
                {(p.payload.items ?? []).map((i) => `${i.cantidad}× ${i.nombre ?? ''}`).join(' · ')}
                {' '}({money((p.payload.items ?? []).reduce((a, i) => a + (Number(i.precio) || 0) * Number(i.cantidad), 0))})
              </span>
              {p.cuenta_codigo && <span className="text-xs font-mono text-primary">{p.cuenta_codigo}</span>}
              <span className="ml-auto flex gap-1.5">
                {p.estado === 'nuevo' && (
                  <>
                    <Button size="sm" onClick={() => void aceptar(p)}><Check className="w-4 h-4 mr-1" /> Aceptar</Button>
                    <Button size="sm" variant="ghost" onClick={() => void rpc('rest_pedido_ext_estado', { _id: p.id, _estado: 'rechazado' }, 'Pedido rechazado')}>
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </>
                )}
                {p.estado === 'aceptado' && (
                  <Button size="sm" variant="outline" onClick={() => void rpc('rest_pedido_ext_estado', { _id: p.id, _estado: 'despachado' }, 'Despachado')}>
                    Despachado
                  </Button>
                )}
                {p.estado === 'despachado' && (
                  <Button size="sm" variant="outline" onClick={() => void rpc('rest_pedido_ext_estado', { _id: p.id, _estado: 'entregado' }, 'Entregado ✔')}>
                    Entregado
                  </Button>
                )}
              </span>
            </div>
          ))}
          {!pedidos.length && (
            <div className="py-6 text-center text-muted-foreground text-sm">
              Sin pedidos externos. Configura un proveedor abajo y usa «Simular pedido» para probar el flujo.
            </div>
          )}
          <div className="flex gap-2 border-t pt-2">
            {config.filter((c) => c.activo && c.modo === 'simulado').map((c) => (
              <Button key={c.id} size="sm" variant="outline"
                      onClick={() => void rpc('rest_pedido_ext_simular', { _location: locationId, _proveedor: c.proveedor }, `Pedido ${c.proveedor} simulado`)}>
                <FlaskConical className="w-4 h-4 mr-1" /> Simular {c.proveedor}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Proveedores del local</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground border-b">
                <th className="py-2 pr-3 text-right w-10">N°</th>
                <th className="py-2 pr-3 text-left">Proveedor</th>
                <th className="py-2 pr-3 text-left">Modo</th>
                <th className="py-2 pr-3 text-center">API key</th>
                <th className="py-2 pr-3 text-right">Comisión %</th>
                <th className="py-2 pr-3 text-center">Activo</th>
              </tr>
            </thead>
            <tbody>
              {config.map((c, i) => (
                <tr key={c.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-medium capitalize">{c.proveedor}</td>
                  <td className="py-2 pr-3 uppercase text-xs">{c.modo}</td>
                  <td className="py-2 pr-3 text-center">{c.api_key_configurada ? '••••' : '—'}</td>
                  <td className="py-2 pr-3 text-right">{c.comision_pct}%</td>
                  <td className="py-2 pr-3 text-center">
                    <Switch checked={c.activo} label=""
                            onChange={(v) => void rpc('rest_agregador_set',
                              { _location: locationId, _data: { proveedor: c.proveedor, activo: v } }, 'Proveedor actualizado')} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border rounded-md p-3 grid gap-3 md:grid-cols-5 items-end">
            <Campo label="Proveedor">
              <select className={fieldCls} value={fProv} onChange={(e) => setFProv(e.target.value)}>
                {PROVEEDORES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Campo>
            <Campo label="Modo">
              <select className={fieldCls} value={fModo} onChange={(e) => setFModo(e.target.value)}>
                <option value="simulado">Simulado (pruebas)</option>
                <option value="directo">Directo (requiere credenciales)</option>
              </select>
            </Campo>
            <Campo label="API key (vacía = conservar)">
              <input className={fieldCls} type="password" autoComplete="new-password"
                     value={fKey} onChange={(e) => setFKey(e.target.value)} />
            </Campo>
            <Campo label="Comisión %">
              <input className={fieldCls} type="number" min="0" max="50" step="0.5"
                     value={fCom} onChange={(e) => setFCom(e.target.value)} />
            </Campo>
            <Button onClick={() => {
              void rpc('rest_agregador_set', { _location: locationId, _data: {
                proveedor: fProv, modo: fModo, api_key: fKey || null, comision_pct: Number(fCom) || 0, activo: true,
              } }, 'Proveedor guardado (clave cifrada)');
              setFKey('');
            }}>Guardar</Button>
          </div>
          <p className="text-xs text-muted-foreground">
            El conector REAL (webhook del agregador → esta bandeja) requiere el contrato de partner con cada
            plataforma; mientras tanto el modo simulado permite entrenar el flujo completo: aceptar → cuenta
            delivery → comandar desde el Salón → despachar → entregado. La comisión sirve para conciliar la
            liquidación del agregador.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
